import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { URL_BASE } from '../../core/constants';
import { UserInfo } from './user-info.model';
import { HttpUtils } from '../../shared/http-utils';
import { Observable } from 'rxjs';
import { UserCacheService, UserListType } from './user-cache.service';
import * as moment from 'moment';
import { map, tap } from 'rxjs/operators';
import { SearchObjectResult } from '../../shared/search-object-result.model';
import {UserSearchCriteria} from '../../shared/user-search-criteria';

@Injectable()
export class DriverService {

  private DRIVERS_URI   = 'api/drivers';
  private cacheListType = UserListType.DRIVERS;

  constructor(private http: HttpClient, private cacheService: UserCacheService) {
  }

  public getByOwnCarrier(): Observable<UserInfo[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.DRIVERS_URI}`;
    return this.http.get(url, { headers })
      .pipe(
        map((users: any[]) => users.map(user => UserInfo.fromDto(user))),
        map(users => this.cacheService.aggregate(users, this.cacheListType))
      );
  }

  public searchByOwnCarrier(page: number, pageSize: number, filters: UserSearchCriteria): Observable<SearchObjectResult<UserInfo>> {
    const headers = HttpUtils.createHeaders();
    const params = filters.toQueryParams(page, pageSize);

    const url     = `${URL_BASE}/${this.DRIVERS_URI}/self/search`;
    return this.http.get(url, { headers, params })
      .pipe(
        map(result => SearchObjectResult.fromDto(result, UserInfo.fromDto)),
      );
  }

  public getByCarrier(carrierId: string): Observable<UserInfo[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.DRIVERS_URI}/carrier/${carrierId}`;
    return this.http.get(url, { headers })
      .pipe(
        map((users: any[]) => users.map(user => UserInfo.fromDto(user))),
        map(users => this.cacheService.aggregate(users, this.cacheListType, (u) => u.carrier && u.carrier.id === carrierId))
      );
  }

  public searchByCarrier(carrierId: string, page: number, pageSize: number, filters: UserSearchCriteria): Observable<SearchObjectResult<UserInfo>> {
    const headers = HttpUtils.createHeaders();
    const params = filters.toQueryParams(page, pageSize);

    const url     = `${URL_BASE}/${this.DRIVERS_URI}/carrier/${carrierId}/search`;
    return this.http.get(url, { headers, params })
      .pipe(
        map(result => SearchObjectResult.fromDto(result, UserInfo.fromDto)),
      );
  }

  public getBySubcontractor(subcontractorId: string): Observable<UserInfo[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/subcontractors/${subcontractorId}/drivers`;
    return this.http.get(url, { headers })
      .pipe(
        map((users: any[]) => users.map(user => UserInfo.fromDto(user))),
        map(users => this.cacheService.aggregate(
          users,
          this.cacheListType, (u) => u.subcontractor && u.subcontractor.id === subcontractorId
        ))
      );
  }

  public searchBySubcontractor(subcontractorId: string, page: number, pageSize: number, filters: UserSearchCriteria): Observable<SearchObjectResult<UserInfo>> {
    const headers = HttpUtils.createHeaders();
    const params = filters.toQueryParams(page, pageSize);

    const url     = `${URL_BASE}/api/subcontractors/${subcontractorId}/drivers/search`;
    return this.http.get(url, { headers, params })
      .pipe(
        map(result => SearchObjectResult.fromDto(result, UserInfo.fromDto)),
      );
  }

  public getById(id: string): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.DRIVERS_URI}/${id}`;
    return this.http.get(url, { headers })
      .pipe(
        map(user => UserInfo.fromDto(user))
      );
  }

  public createForSelfCarrier(driver: UserInfo): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.DRIVERS_URI}`;
    return this.http.post(url, UserInfo.toDto(driver), { headers })
      .pipe(
        map(u => UserInfo.fromDto(u)),
        tap(u => this.cacheService.registerNewUser(u, this.cacheListType))
      );
  }

  public createForCarrier(driver: UserInfo, carrierId: string): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.DRIVERS_URI}/carrier/${carrierId}`;
    return this.http.post(url, UserInfo.toDto(driver), { headers })
      .pipe(
        map(u => UserInfo.fromDto(u)),
        tap(u => this.cacheService.registerNewUser(u, this.cacheListType))
      );
  }

  public createForSubcontractor(driver: UserInfo, subcontractorId: string): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/subcontractors/${subcontractorId}/drivers`;
    return this.http.post(url, UserInfo.toDto(driver), { headers })
      .pipe(
        map(u => UserInfo.fromDto(u)),
        tap(u => this.cacheService.registerNewUser(u, this.cacheListType))
      );
  }

  public update(driver: UserInfo): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.DRIVERS_URI}/${driver.id}`;
    return this.http.put(url, UserInfo.toDto(driver), { headers })
      .pipe(
        map(u => UserInfo.fromDto(u)),
        tap(u => this.cacheService.registerUpdatedUser(u, this.cacheListType))
      );
  }

  public updatePassword(driver: UserInfo, password: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.DRIVERS_URI}/${driver.id}/password`;
    return this.http.post<void>(url, { password }, { headers });
  }

  public delete(driverId: string): Observable<boolean> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.DRIVERS_URI}/${driverId}`;
    return this.http.delete<boolean>(url, { headers })
      .pipe(
        tap(() => this.cacheService.registerRemovedUser(driverId, this.cacheListType))
      );
  }

  public toggleBlocking(driver: UserInfo): Observable<boolean> {
    const updateDate = moment();

    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.DRIVERS_URI}/${driver.id}/blocked/${!driver.blocked}`;
    return this.http.post<boolean>(url, null, { headers })
      .pipe(
        map(result => {
          driver.updateDate          = updateDate.toISOString();
          driver.updateDateTimestamp = Number(updateDate.format('x'));
          this.cacheService.registerUpdatedUser(driver, this.cacheListType);
          return result;
        })
      );
  }

  public unblockIp(driver: UserInfo): Observable<boolean> {
    const updateDate = moment();

    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.DRIVERS_URI}/${driver.id}/unblockFromAnomaly`;
    return this.http.post<boolean>(url, null, { headers })
      .pipe(
        tap(() => {
          driver.updateDate          = updateDate.toISOString();
          driver.updateDateTimestamp = Number(updateDate.format('x'));
          this.cacheService.registerUpdatedUser(driver, this.cacheListType);
        })
      );
  }

}
